"use client";

import { useState, useCallback } from "react";
import { CountryCode, parsePhoneNumber } from "libphonenumber-js";
import {
  usePhoneValidation,
  PhoneValidationResult,
} from "@/hooks/usePhoneValidation";
import { phoneMessage } from "@/lib/phoneMessages";

interface SimplePhoneInputProps {
  value: string;
  onChange: (
    value: string,
    validationResult: PhoneValidationResult & { countryCode?: CountryCode }
  ) => void;
  hasError?: boolean;
  placeholder?: string;
  required?: boolean;
  label?: string;
  className?: string;
  /** País pré-selecionado no seletor de DDI (o utilizador pode trocar). */
  defaultCountry?: CountryCode;
}

// Lista simplificada dos principais países
const COUNTRIES: Array<{
  code: CountryCode;
  name: string;
  flag: string;
  dialCode: string;
}> = [
  { code: "BR", name: "Brasil", flag: "🇧🇷", dialCode: "+55" },
  { code: "US", name: "Estados Unidos", flag: "🇺🇸", dialCode: "+1" },
  { code: "AR", name: "Argentina", flag: "🇦🇷", dialCode: "+54" },
  { code: "PT", name: "Portugal", flag: "🇵🇹", dialCode: "+351" },
  { code: "ES", name: "Espanha", flag: "🇪🇸", dialCode: "+34" },
  { code: "MX", name: "México", flag: "🇲🇽", dialCode: "+52" },
  { code: "CO", name: "Colômbia", flag: "🇨🇴", dialCode: "+57" },
  { code: "CL", name: "Chile", flag: "🇨🇱", dialCode: "+56" },
];

function countryFromInternationalValue(
  value: string,
  fallback: CountryCode,
): CountryCode {
  if (!value.trim().startsWith("+")) return fallback;

  try {
    const country = parsePhoneNumber(value).country;
    return country && COUNTRIES.some((item) => item.code === country)
      ? country
      : fallback;
  } catch {
    return fallback;
  }
}

export function SimplePhoneInput({
  value,
  onChange,
  hasError = false,
  placeholder = "(11) 99999-9999",
  required = false,
  label,
  className = "",
  defaultCountry = "BR",
}: SimplePhoneInputProps) {
  const [countryPreference, setSelectedCountry] = useState<CountryCode>(() =>
    countryFromInternationalValue(value, defaultCountry),
  );
  const selectedCountry = countryFromInternationalValue(value, countryPreference);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { validatePhone } = usePhoneValidation();
  const validationResult = value ? validatePhone(value, selectedCountry) : null;

  // Encontra o país selecionado
  const currentCountry =
    COUNTRIES.find((c) => c.code === selectedCountry) || COUNTRIES[0];

  const formatBrazilianWhatsAppNumber = useCallback((input: string) => {
    const rawDigits = input.replace(/\D/g, "");
    const digits =
      rawDigits.length > 11 ? rawDigits.slice(-11) : rawDigits.slice(0, 11);

    if (digits.length === 0) return "";
    if (digits.length < 3) return `(${digits}`;
    if (digits.length < 8) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }, []);

  const handlePhoneChange = useCallback(
    (inputValue: string) => {
      const normalizedValue =
        selectedCountry === "BR"
          ? formatBrazilianWhatsAppNumber(inputValue)
          : inputValue;
      const result = validatePhone(normalizedValue, selectedCountry);
      onChange(normalizedValue, { ...result, countryCode: selectedCountry });
    },
    [selectedCountry, validatePhone, onChange, formatBrazilianWhatsAppNumber]
  );

  const handleCountryChange = (countryCode: CountryCode) => {
    setSelectedCountry(countryCode);
    setIsDropdownOpen(false);

    // Re-valida o número com o novo país se já existir um valor
    if (value) {
      const result = validatePhone(value, countryCode);
      onChange(value, { ...result, countryCode });
    }
  };

  // Determina se deve mostrar feedback visual
  const shouldShowValidation =
    value.trim() !== "" && value.replace(/\D/g, "").length >= 8;
  const isValid = validationResult?.isValid || false;
  const hasValidationError =
    shouldShowValidation &&
    !isValid &&
    validationResult?.errorMessage !== phoneMessage("keepTyping");

  return (
    <div className="space-y-2">
      {label && (
        <label className="form-label-compact">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative flex gap-2">
        {/* Seletor de país */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="h-full px-3 border border-[rgba(232,115,115,0.3)] bg-white/90 hover:bg-[rgba(253,242,242,0.5)] 
                       rounded-md flex items-center space-x-1 transition-colors duration-200
                       focus:outline-none focus:ring-2 focus:ring-tarot-dourado/50 
                       min-w-[90px] form-input-compact"
            aria-label="Selecionar país"
          >
            <span className="text-base">{currentCountry.flag}</span>
            <span className="text-xs text-[#4a2c2c] font-mono">
              {currentCountry.dialCode}
            </span>
            <svg
              className={`w-3 h-3 text-[#4a2c2c] transition-transform duration-200 ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Dropdown de países */}
          {isDropdownOpen && (
            <>
              {/* Overlay para fechar dropdown */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsDropdownOpen(false)}
              />

              <div
                className="absolute top-full left-0 mt-1 w-80 bg-white border border-[rgba(232,115,115,0.3)] 
                             rounded-md shadow-xl max-h-64 overflow-y-auto z-50"
              >
                {COUNTRIES.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountryChange(country.code)}
                    className={`w-full px-4 py-3 text-left text-sm hover:bg-[rgba(253,242,242,0.5)] flex items-center space-x-3
                               transition-colors duration-150 ${
                                 country.code === selectedCountry
                                   ? "bg-[rgba(253,242,242,0.8)] border-l-3 border-[#800000] font-medium"
                                   : ""
                               }`}
                  >
                    <span className="text-lg">{country.flag}</span>
                    <span className="flex-1">{country.name}</span>
                    <span className="text-[#4a2c2c] font-mono text-xs">
                      {country.dialCode}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex-1">
          <input
            type="tel"
            value={value}
            onChange={(e) => handlePhoneChange(e.target.value)}
            className={`form-input-compact w-full pl-4 focus:outline-none transition-all duration-300 rounded-md ${
              hasError
                ? "border-red-500 focus:border-red-600 bg-red-50"
                : shouldShowValidation
                ? isValid
                  ? "border-green-500 focus:border-green-600 bg-green-50/30"
                  : hasValidationError
                  ? "border-yellow-400 focus:border-yellow-500 bg-yellow-50/50"
                  : "focus:border-tarot-dourado"
                : "focus:border-tarot-dourado"
            } ${className}`}
            placeholder={placeholder}
            autoComplete="tel"
            maxLength={selectedCountry === "BR" ? 15 : undefined}
          />
        </div>
      </div>

      {/* Feedback de validação */}
      {shouldShowValidation && (
        <div className="space-y-1">
          {isValid ? (
            <>
              <div className="flex items-center text-green-600 text-xs font-medium">
                <svg
                  className="mr-2 h-3.5 w-3.5 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20 6 9 17l-5-5"
                  />
                </svg>
                <span>{phoneMessage("valid")}</span>
              </div>
            </>
          ) : (
            hasValidationError && (
              <div className="flex items-center text-yellow-600 text-xs font-medium">
                <span className="mr-2">⚠️</span>
                <span>{validationResult?.errorMessage}</span>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
